import React, { useEffect, useRef, FC, useState, useMemo } from "react";
import { Accordion, Heading, Skeleton, Notification, Icon, Tabs } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import { ICartItem, RootState } from "../../../../../../models";
import { VasOffering } from "../../../../../../models/state";
import { CART_ITEM_TYPE } from "../../../../../../constants";
import { removeItemFromCart, removeServiceFromCart } from "../../../../checkout/state/actions/cartActions";
import { getItemByBundleId } from "../../../../checkout/state/selectors/cartSelectors";
import { fetchAdditionalServices as fetchAdditionalServicesAction } from "../../../state/actions";
import {
    getAddedAdditionalProducts,
    getAdditionalProducts,
    getAdditionalProductsError,
    getAdditionalProductsLoading,
} from "../../../state/selectors/additionalProductsSelectors";
import { RetailerSubscriptionSelectable, SimConfig } from "../RetailerSubscriptionSelectable";
import "./SubscriptionAdditionalServices.less";

interface Props {
    isExisting?: boolean;
    phoneNumber: string;
    subscriptionId: string;
    isLoading: boolean;
    subscriptionIsEsim?: boolean;
    handleAddServiceToCart: (service: VasOffering, simConfig?: SimConfig) => void;
}

const POPULAR_VAS_CODES = [
    "SECRET_NUMBER", // Hemmelig nummer
];

const isVoicemailCode = (code: string): boolean => code.startsWith("VOICEMAIL");
const isFirstTvillingSim = (code: string): boolean => code.startsWith("MULTI_TVILLING_SIM_A");
const isFirstDataSim = (code: string): boolean => code.startsWith("MULTI_DATA_SIM_A");
const isExtraDataSimCategory = (additionalProduct: VasOffering): boolean =>
    additionalProduct.category === "MULTI_SIM" && additionalProduct.simType === "DATASIM";
const isExtraTvillingSimCategory = (additionalProduct: VasOffering): boolean =>
    additionalProduct.category === "MULTI_SIM" && additionalProduct.simType === "TVILLINGSIM";
const isExtraKlokkeTvillingSimCategory = (additionalProduct: VasOffering): boolean =>
    additionalProduct.category === "MULTI_SIM" && additionalProduct.simType === "TVILLINGSIM_KLOKKE";

const getExtraSimDisplayName = (product: VasOffering): string => {
    if (isExtraDataSimCategory(product)) {
        return "Ekstra dataSIM";
    }
    if (isExtraTvillingSimCategory(product)) {
        return "Ekstra TvillingSIM";
    }
    if (isExtraKlokkeTvillingSimCategory(product)) {
        return "Ekstra Klokke TvillingSIM";
    }
    return product.name;
};

const getCategoryId = (additionalProduct: VasOffering): string => {
    if (additionalProduct.category === "VAS" && additionalProduct.code.includes("INSURANCE")) {
        return "INSURANCE";
    }
    if (
        POPULAR_VAS_CODES.includes(additionalProduct.code) ||
        isVoicemailCode(additionalProduct.code) ||
        isExtraDataSimCategory(additionalProduct) ||
        isExtraTvillingSimCategory(additionalProduct)
    ) {
        return "POPULAR";
    }
    return additionalProduct.category || "OTHER";
};

const CATEGORY_ORDER = ["VAS", "MULTI_SIM", "SERVICE", "BARRING", "INSURANCE", "PROMO", "OTHER"];

const HIDDEN_CATEGORIES = ["GET", "LEAS_FLEX"];

const getCategoryName = (categoryId: string): string => {
    switch (categoryId) {
        case "INSURANCE":
            return "Forsikringer";
        case "POPULAR":
            return "Utvalgte";
        case "SERVICE":
            return "Mobilbruk";
        case "BARRING":
            return "Sperringer";
        case "MULTI_SIM":
            return "SIM-kort";
        case "PROMO":
            return "Kampanjer";
        case "OTHER":
            return "Øvrige";
        case "VAS":
            return "Populære";
        default:
            return categoryId;
    }
};

// Backend has Datasim1, Datasim2 etc. as codes for multi sims. We want to only present one item to the user.
const filterAdditionalProducts = (products: VasOffering[]): VasOffering[] => {
    let seenExtraDataSim = false;
    let seenExtraTvillingSim = false;
    let seenExtraKlokkeTvillingSim = false;

    return products.reduce<VasOffering[]>((filteredProducts, product) => {
        if (isExtraDataSimCategory(product)) {
            if (!seenExtraDataSim) {
                seenExtraDataSim = true;
                filteredProducts.push({
                    ...product,
                    name: getExtraSimDisplayName(product),
                    canAddMultiple: true,
                });
            }
            return filteredProducts;
        }
        if (isExtraTvillingSimCategory(product)) {
            if (!seenExtraTvillingSim) {
                seenExtraTvillingSim = true;
                filteredProducts.push({
                    ...product,
                    name: getExtraSimDisplayName(product),
                    canAddMultiple: true,
                });
            }
            return filteredProducts;
        }
        if (isExtraKlokkeTvillingSimCategory(product)) {
            if (!seenExtraKlokkeTvillingSim) {
                seenExtraKlokkeTvillingSim = true;
                filteredProducts.push({
                    ...product,
                    name: getExtraSimDisplayName(product),
                    canAddMultiple: true,
                });
            }
            return filteredProducts;
        }
        filteredProducts.push(product);
        return filteredProducts;
    }, []);
};

const SubscriptionAdditionalServices: FC<Props> = ({
    subscriptionId,
    phoneNumber,
    isExisting,
    handleAddServiceToCart,
    isLoading,
}) => {
    const dispatch = useDispatch();
    const [activeCategory, setActiveCategory] = useState("");
    const initializedForSubscriptionRef = useRef<string | null>(null);
    const activeCategoryInitializedForRef = useRef<string | null>(null);
    const additionalProductsRaw = useSelector((state: RootState) => getAdditionalProducts(state, subscriptionId));
    const additionalProducts = useMemo(() => filterAdditionalProducts(additionalProductsRaw), [additionalProductsRaw]);
    const isFetchingAdditionalProducts = useSelector((state: RootState) =>
        getAdditionalProductsLoading(state, subscriptionId),
    );
    const additionalProductsError = useSelector((state: RootState) =>
        getAdditionalProductsError(state, subscriptionId),
    );

    const addedAdditionalProducts = useSelector((state: RootState) =>
        getAddedAdditionalProducts(state, subscriptionId),
    );
    const hasHandset = useSelector((state: RootState) =>
        getItemByBundleId(state, subscriptionId).some(
            (item) => item.type === CART_ITEM_TYPE.HANDSET || item.type === CART_ITEM_TYPE.HANDSET_PREOWNED,
        ),
    );

    const modifiableProducts = useMemo(
        () => additionalProducts?.filter((product) => !product.included) || [],
        [additionalProducts],
    );
    const includedProducts = useMemo(
        () => additionalProducts?.filter((product) => product.included) || [],
        [additionalProducts],
    );

    const addedCodes = useMemo(
        () => new Set(addedAdditionalProducts.map((product) => product.code)),
        [addedAdditionalProducts],
    );

    const addedProductsWithMetadata = useMemo(
        () =>
            addedAdditionalProducts.map((added) => {
                const withMetadata = additionalProducts?.find((p) => p.code === added.code);
                return withMetadata || added;
            }),
        [addedAdditionalProducts, additionalProducts],
    );

    // Filter out hidden categories and insurance products (when no handset) so render
    // conditions reflect what's actually visible — consistent with productsGroupedByCategory
    const visibleModifiableProducts = useMemo(() => {
        return modifiableProducts.filter((product) => {
            if (HIDDEN_CATEGORIES.includes(product.category)) return false;
            if (getCategoryId(product) === "INSURANCE" && !hasHandset) return false;
            return true;
        });
    }, [modifiableProducts, hasHandset]);

    const productsGroupedByCategory = useMemo(() => {
        const grouped: { [category: string]: VasOffering[] } = {};
        modifiableProducts.forEach((product) => {
            const category = getCategoryId(product);
            if (category === "POPULAR") {
                return;
            }
            if (HIDDEN_CATEGORIES.includes(product.category)) {
                return;
            }
            if (category === "INSURANCE" && !hasHandset) {
                return;
            }
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(product);
        });
        const sortedKeys = [
            ...CATEGORY_ORDER.filter((cat) => cat in grouped),
            ...Object.keys(grouped).filter((cat) => !CATEGORY_ORDER.includes(cat)),
        ];
        return Object.fromEntries(sortedKeys.map((key) => [key, grouped[key]]));
    }, [modifiableProducts, hasHandset]);

    const popularProducts = useMemo(() => {
        const getOrder = (code: string): number => {
            if (isVoicemailCode(code)) return 0; // Personsvar
            if (code === "SECRET_NUMBER") return 1; // Hemmelig nummer
            if (isFirstDataSim(code)) return 2; // Ekstra DataSIM
            if (isFirstTvillingSim(code)) return 3; //Ekstra TvillingSIM
            return 4;
        };
        return modifiableProducts
            .filter((product) => getCategoryId(product) === "POPULAR")
            .sort((a, b) => getOrder(a.code) - getOrder(b.code));
    }, [modifiableProducts]);

    useEffect(() => {
        // Reset initialization when subscription changes so new products get pre-selected
        initializedForSubscriptionRef.current = null;
        activeCategoryInitializedForRef.current = null;
        setActiveCategory("");
    }, [subscriptionId, phoneNumber]);

    useEffect(() => {
        // Set active category to the first available category in CATEGORY_ORDER once products load
        const categories = Object.keys(productsGroupedByCategory);
        if (categories.length === 0) return;
        if (activeCategoryInitializedForRef.current === subscriptionId) return;
        activeCategoryInitializedForRef.current = subscriptionId;
        const firstCategory = CATEGORY_ORDER.find((cat) => categories.includes(cat)) ?? categories[0];
        setActiveCategory(firstCategory);
    }, [productsGroupedByCategory, subscriptionId]);

    useEffect(() => {
        const fetchAdditionalProducts = async () => {
            if (subscriptionId && phoneNumber) {
                await dispatch(
                    fetchAdditionalServicesAction(subscriptionId, phoneNumber, isExisting ? undefined : subscriptionId),
                );
            }
        };
        fetchAdditionalProducts();
    }, [dispatch, subscriptionId, phoneNumber, isExisting]);

    const canAddProduct = (id: string): boolean => {
        return !addedProductsWithMetadata.some(
            (product) =>
                product.code === id || product.excludedProducts?.some((code) => code === id && product.code !== id),
        );
    };

    const getDisabledReason = (id: string): string | undefined => {
        const conflicting = addedProductsWithMetadata.filter(
            (product) => product.excludedProducts?.includes(id) && product.code !== id,
        );
        if (conflicting.length === 0) return undefined;
        const names = conflicting.map((p) => p.name).join(", ");
        return `Denne tjenesten kan ikke kombineres med ${names}`;
    };

    const handleRemoveServiceFromCart = (service: VasOffering) => {
        if (isExisting) {
            return dispatch(removeServiceFromCart(service.code, undefined, phoneNumber));
        }
        // oxlint-disable-next-line typescript/ban-ts-comment
        dispatch(
            // @ts-expect-error removeItemFromCart returns a union; dispatch handles it at runtime
            removeItemFromCart({
                type: CART_ITEM_TYPE.SERVICE,
                id: service.code,
                parentId: subscriptionId,
            } as unknown as ICartItem),
        );
    };

    const noResults =
        !isFetchingAdditionalProducts &&
        includedProducts.length === 0 &&
        visibleModifiableProducts.length === 0 &&
        !additionalProductsError &&
        phoneNumber;

    return (
        <div className="subscription-additional-services">
            {additionalProductsError && (
                <Notification type="tile" variant="error" mb="400">
                    <Notification.Icon />
                    <Notification.Text>{additionalProductsError}</Notification.Text>
                </Notification>
            )}
            {!phoneNumber && (
                <Notification type="tile" variant="warning" mb="300">
                    <Notification.Icon />
                    <Notification.Text>Sørg for at du har lagt til et telefonnummer for abonnementet</Notification.Text>
                </Notification>
            )}
            {noResults ? (
                <>
                    <Heading as="h3" variant="title-100" mt="400">
                        Andre tjenester
                    </Heading>
                    <Notification type="tile" variant="gray" mt="200" mb="100">
                        <Notification.Icon name="alert-filled" />
                        <Notification.Text>
                            Ingen tilleggstjenester tilgjengelig for dette abonnementet.
                        </Notification.Text>
                    </Notification>
                </>
            ) : (
                <>
                    {!isFetchingAdditionalProducts && includedProducts.length > 0 && (
                        <>
                            <Heading mb="100" as="h4" variant="title-100">
                                Inkludert i valgt abonnement
                            </Heading>
                            <div className="subscription-additional-services__container">
                                <div className="subscription-additional-services__included-services">
                                    <ul>
                                        {includedProducts.map((service: VasOffering) => {
                                            return (
                                                <li key={service.code}>
                                                    <Icon
                                                        name="check-circle"
                                                        size="md"
                                                        className="subscription-additional-services__included-services__icon"
                                                        aria-hidden="true"
                                                        focusable="false"
                                                    />
                                                    {service.name}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </>
                    )}

                    {(isFetchingAdditionalProducts || visibleModifiableProducts.length > 0) && (
                        <>
                            {popularProducts.length > 0 && (
                                <Heading as="h3" variant="title-100" mt="400">
                                    Utvalgte tjenester
                                </Heading>
                            )}
                            <div className="subscription-additional-services__container">
                                {isFetchingAdditionalProducts &&
                                    Array.from({ length: 3 }).map((_unused, index) => (
                                        <Skeleton radius="sm" height="82px" width="100%" key={index} />
                                    ))}
                                {!isFetchingAdditionalProducts &&
                                    popularProducts.map((service: VasOffering) => {
                                        const isInCart = addedCodes.has(service.code);
                                        return (
                                            <RetailerSubscriptionSelectable
                                                key={service.code}
                                                id={service.code}
                                                handleAddServiceToCart={(simConfig) => {
                                                    handleAddServiceToCart(service, simConfig);
                                                }}
                                                handleRemoveFromCart={
                                                    isInCart && !service.canAddMultiple
                                                        ? () => handleRemoveServiceFromCart(service)
                                                        : undefined
                                                }
                                                name={service.name}
                                                isLoading={isLoading}
                                                price={service.monthlyPrice || 0}
                                                isDisabled={!isInCart && !canAddProduct(service.code)}
                                                disabledReason={isInCart ? undefined : getDisabledReason(service.code)}
                                                description={service.description}
                                                simType={service.simType}
                                            />
                                        );
                                    })}
                            </div>
                            {Object.keys(productsGroupedByCategory).length > 0 && (
                                <Accordion
                                    type="single"
                                    collapsible
                                    defaultValue={popularProducts.length === 0 ? "andre-tjenester" : undefined}
                                    style={{ marginTop: "1rem" }}
                                >
                                    <Accordion.Item value="andre-tjenester">
                                        <Accordion.Header>
                                            <Accordion.Trigger>
                                                <Heading mb="100" as="h3" variant="title-100">
                                                    Andre tjenester
                                                </Heading>
                                            </Accordion.Trigger>
                                        </Accordion.Header>
                                        <Accordion.Content>
                                            <Tabs
                                                value={activeCategory}
                                                onValueChange={(value) => setActiveCategory(value)}
                                                style={{ marginTop: "var(--teddy-spacing-200)" }}
                                            >
                                                <Tabs.List>
                                                    {Object.keys(productsGroupedByCategory).map((category) => (
                                                        <Tabs.Trigger key={category} value={category}>
                                                            {getCategoryName(category)}
                                                        </Tabs.Trigger>
                                                    ))}
                                                </Tabs.List>
                                                {Object.keys(productsGroupedByCategory).map((category) => (
                                                    <Tabs.Content key={category} value={category}>
                                                        <div className="subscription-additional-services__container">
                                                            {(productsGroupedByCategory[category] || [])
                                                                .sort((a, b) => a.name.localeCompare(b.name))
                                                                .map((service: VasOffering) => {
                                                                    const isInCart = addedCodes.has(service.code);
                                                                    return (
                                                                        <RetailerSubscriptionSelectable
                                                                            key={service.code}
                                                                            id={service.code}
                                                                            handleAddServiceToCart={(simConfig) =>
                                                                                handleAddServiceToCart(
                                                                                    service,
                                                                                    simConfig,
                                                                                )
                                                                            }
                                                                            handleRemoveFromCart={
                                                                                isInCart
                                                                                    ? () =>
                                                                                          handleRemoveServiceFromCart(
                                                                                              service,
                                                                                          )
                                                                                    : undefined
                                                                            }
                                                                            isLoading={isLoading}
                                                                            name={service.name}
                                                                            price={service.monthlyPrice || 0}
                                                                            isDisabled={
                                                                                !isInCart &&
                                                                                !canAddProduct(service.code)
                                                                            }
                                                                            disabledReason={
                                                                                isInCart
                                                                                    ? undefined
                                                                                    : getDisabledReason(service.code)
                                                                            }
                                                                            description={service.description}
                                                                            simType={service.simType}
                                                                        />
                                                                    );
                                                                })}
                                                        </div>
                                                    </Tabs.Content>
                                                ))}
                                            </Tabs>
                                        </Accordion.Content>
                                    </Accordion.Item>
                                </Accordion>
                            )}
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default SubscriptionAdditionalServices;
